import React, { useState, useEffect } from 'react';
import { X, Plus, RefreshCw, GraduationCap } from 'lucide-react';
import { configAPI } from '../../../../services/api';
import toast from '../../../../utils/toast';
import Portal from '../../../../components/UI/Portal';

const EducationInstitutionForm = ({ institution, isOpen, onClose, onSave, institutionTypes = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    institution_type: '',
    address: '',
    postal_code: '',
    website: '',
    contact_phone: '',
    contact_email: '',
    alpha_two_code: 'SG',
    moe_code: '',
    uen: '',
    is_active: true,
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (institution) {
      setFormData({
        name: institution.name || '',
        institution_type: institution.institution_type || '',
        address: institution.address || '',
        postal_code: institution.postal_code || '',
        website: institution.website || '',
        contact_phone: institution.contact_phone || '',
        contact_email: institution.contact_email || '',
        alpha_two_code: institution.alpha_two_code || 'SG',
        moe_code: institution.moe_code || '',
        uen: institution.uen || '',
        is_active: institution.is_active !== undefined ? institution.is_active : true,
        notes: institution.notes || ''
      });
    } else {
      setFormData({
        name: '',
        institution_type: '',
        address: '',
        postal_code: '',
        website: '',
        contact_phone: '',
        contact_email: '',
        alpha_two_code: 'SG',
        moe_code: '',
        uen: '',
        is_active: true,
        notes: ''
      });
    }
    setErrors({});
  }, [institution, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name) newErrors.name = 'Institution name is required';

    // Email validation
    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }

    // URL validation
    if (formData.website && !/^https?:\/\/.+\..+/.test(formData.website)) {
      newErrors.website = 'Invalid URL format';
    }

    // Alpha two code validation (2 characters)
    if (formData.alpha_two_code && formData.alpha_two_code.length !== 2) {
      newErrors.alpha_two_code = 'Country code must be 2 characters (ISO 3166-1 alpha-2)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    try {
      const url = institution 
        ? `/api/v1/configuration/education-institutions/${institution.id}/`
        : '/api/v1/configuration/education-institutions/';
      
      const method = institution ? 'PATCH' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Failed to save institution');
      }

      toast.success(institution ? '✅ Institution updated successfully' : '✅ Institution added successfully');
      onSave && onSave();
    } catch (error) {
      console.error('Error saving institution:', error);
      toast.error(error.message || 'Failed to save institution');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-red-600" />
              <h3 className="modal-title">
                {institution ? 'Edit Education Institution' : 'Add Education Institution'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="modal-close-admin"
              disabled={saving}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
            <div className="modal-content">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Basic Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Institution Name *
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className={`input-admin w-full ${errors.name ? 'border-red-500' : ''}`}
                        placeholder="Official institution name"
                      />
                      {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Institution Type
                      </label>
                      <select
                        value={formData.institution_type}
                        onChange={(e) => setFormData({...formData, institution_type: e.target.value})}
                        className="select-admin w-full"
                      >
                        <option value="">Select type...</option>
                        {institutionTypes.map(type => (
                          <option key={type.id} value={type.id}>{type.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        MOE Code
                      </label>
                      <input
                        type="text"
                        value={formData.moe_code}
                        onChange={(e) => setFormData({...formData, moe_code: e.target.value})}
                        className="input-admin w-full"
                        placeholder="Ministry of Education code"
                      />
                    </div>
                  </div>
                </div>

                {/* Location Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Location</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <textarea
                        value={formData.address}
                        onChange={(e) => setFormData({...formData, address: e.target.value})}
                        rows={2}
                        className="input-admin w-full"
                        placeholder="Physical address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Postal Code
                      </label>
                      <input
                        type="text"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                        className="input-admin w-full"
                        placeholder="Postal code"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Country Code (ISO) *
                      </label>
                      <input
                        type="text"
                        value={formData.alpha_two_code}
                        onChange={(e) => setFormData({...formData, alpha_two_code: e.target.value.toUpperCase().slice(0, 2)})}
                        className={`input-admin w-full ${errors.alpha_two_code ? 'border-red-500' : ''}`}
                        placeholder="SG, MY, US, etc."
                        maxLength={2}
                      />
                      {errors.alpha_two_code && <p className="text-red-500 text-sm mt-1">{errors.alpha_two_code}</p>}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Website
                      </label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className={`input-admin w-full ${errors.website ? 'border-red-500' : ''}`}
                        placeholder="https://www.example.com"
                      />
                      {errors.website && <p className="text-red-500 text-sm mt-1">{errors.website}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Phone
                      </label>
                      <input
                        type="text"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                        className="input-admin w-full"
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                        className={`input-admin w-full ${errors.contact_email ? 'border-red-500' : ''}`}
                        placeholder="contact@institution.edu"
                      />
                      {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
                    </div>
                  </div>
                </div>

                {/* Singapore-Specific Fields */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Singapore-Specific</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        UEN (Optional)
                      </label>
                      <input
                        type="text"
                        value={formData.uen}
                        onChange={(e) => setFormData({...formData, uen: e.target.value.toUpperCase()})}
                        className="input-admin w-full"
                        placeholder="Unique Entity Number"
                        maxLength={12}
                      />
                      <p className="text-xs text-gray-500 mt-1">Singapore Unique Entity Number (if applicable)</p>
                    </div>
                  </div>
                </div>

                {/* Library Management */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Library Management</h4>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        className="h-4 w-4 text-red-600 focus-visible:ring-red-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-900">Active in Library</span>
                    </label>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Internal Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      rows={3}
                      className="input-admin w-full"
                      placeholder="Any internal notes about this institution"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-admin-outline"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-admin-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {institution ? 'Update Institution' : 'Add Institution'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Portal>
  );
};

export default EducationInstitutionForm;

